import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  IconButton,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const initialPolicies = [
  {
    name: "Drugs",
    description: "Content that promotes, glorifies, or facilitates the illegal use, sale, or distribution of drugs is strictly prohibited.",
    examples: [
      "Instructions on drug manufacturing",
      "Promotion of drug paraphernalia",
      "Discussion encouraging illegal drug use"
    ]
  },
  {
    name: "Adult Content",
    description: "Explicit, sexual, or pornographic content that is not age-appropriate or consent-driven is not allowed.",
    examples: [
      "Explicit sexual descriptions intended for titillation"
    ]
  },
  {
    name: "Hate Speech",
    description: "Content promoting violence, discrimination, or hatred based on race, ethnicity, religion, gender, disability, or other protected attributes is prohibited.",
    examples: [
      "Slurs or derogatory language targeting specific groups",
      "Text advocating violence against individuals or groups"
    ]
  },
  {
    name: "Violence and Threats",
    description: "Text that threatens or glorifies violence is not permitted.",
    examples: [
      "Explicit threats of harm",
      "Encouragement of self-harm or suicide",
      "Content celebrating or justifying violence"
    ]
  }
];

const PolicyForm = ({ onUpdate }) => {
  const [policies, setPolicies] = useState([
    {
      name: "Hate Speech",
      description: "Content that expresses hatred or promotes violence against individuals or groups based on attributes such as race, religion, or gender",
      examples: [
        "racial slurs",
        "discriminatory language",
        "calls for violence against groups"
      ]
    },
    {
      name: "Violence and Threats",
      description: "Text that threatens or glorifies violence is not permitted",
      examples: [
        "explicit threats of harm",
        "encouragement of self-harm or suicide",
        "content celebrating or justifying violence"
      ]
    }
  ]);

  const [newExamples, setNewExamples] = useState({});

  useEffect(() => {
    onUpdate(policies);
  }, [onUpdate]);

  const handlePolicyChange = (index, field, value) => {
    const newPolicies = [...policies];
    newPolicies[index] = {
      ...newPolicies[index],
      [field]: value
    };
    setPolicies(newPolicies);
    onUpdate(newPolicies);
  };

  const handleAddPolicy = () => {
    const newPolicies = [...policies, { 
      name: '', 
      description: '', 
      examples: [] 
    }];
    setPolicies(newPolicies);
    onUpdate(newPolicies);
  };

  const handleRemovePolicy = (index) => {
    const newPolicies = policies.filter((_, i) => i !== index);
    setPolicies(newPolicies);
    onUpdate(newPolicies);
    
    const updatedNewExamples = { ...newExamples };
    delete updatedNewExamples[index];
    setNewExamples(updatedNewExamples);
  };

  const handleAddExample = (policyIndex, example) => {
    if (example.trim()) {
      const newPolicies = [...policies];
      newPolicies[policyIndex] = {
        ...newPolicies[policyIndex],
        examples: [...newPolicies[policyIndex].examples, example.trim()]
      };
      setPolicies(newPolicies);
      onUpdate(newPolicies);
      
      setNewExamples({
        ...newExamples,
        [policyIndex]: ''
      });
    }
  };

  const handleRemoveExample = (policyIndex, exampleIndex) => {
    const newPolicies = [...policies];
    newPolicies[policyIndex] = {
      ...newPolicies[policyIndex],
      examples: newPolicies[policyIndex].examples.filter((_, i) => i !== exampleIndex)
    };
    setPolicies(newPolicies);
    onUpdate(newPolicies);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Stack spacing={3}>
        {policies.map((policy, policyIndex) => (
          <Card 
            key={policyIndex}
            variant="outlined"
            sx={{ 
              position: 'relative',
              '&:hover': { boxShadow: 1 },
              bgcolor: 'background.paper',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Policy Name"
                  value={policy.name}
                  onChange={(e) => handlePolicyChange(policyIndex, 'name', e.target.value)}
                  required
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Policy Description"
                  multiline
                  rows={3}
                  value={policy.description}
                  onChange={(e) => handlePolicyChange(policyIndex, 'description', e.target.value)}
                  required
                  variant="outlined"
                />
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Prohibited Examples
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {policy.examples.map((example, exampleIndex) => (
                      <Chip
                        key={exampleIndex}
                        label={example}
                        onDelete={() => handleRemoveExample(policyIndex, exampleIndex)}
                        sx={{
                          bgcolor: '#f5f5f5',
                          '&:hover': { bgcolor: '#eeeeee' },
                        }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add a prohibited example"
                      value={newExamples[policyIndex] || ''}
                      onChange={(e) => setNewExamples({
                        ...newExamples,
                        [policyIndex]: e.target.value
                      })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddExample(policyIndex, newExamples[policyIndex] || '');
                          e.preventDefault();
                        }
                      }}
                      variant="outlined"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleAddExample(policyIndex, newExamples[policyIndex] || '')}
                      sx={{ 
                        minWidth: '40px',
                        borderColor: '#e0e0e0',
                        color: 'text.secondary',
                      }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                </Box>

                {policies.length > 1 && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'error.main'
                    }}
                    onClick={() => handleRemovePolicy(policyIndex)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddPolicy}
          variant="outlined"
          sx={{ 
            borderStyle: 'dashed',
            alignSelf: 'flex-start',
            borderRadius: 2,
            borderColor: '#e0e0e0',
            color: 'text.secondary',
            '&:hover': {
              borderColor: '#bdbdbd',
              bgcolor: 'transparent',
            }
          }}
        >
          Add New Policy
        </Button>
      </Stack>
    </Box>
  );
};

export default PolicyForm; 